import { useCallback, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { Accordion } from "react-bootstrap";
import { Col, Row } from "reactstrap";
import DataFlowImage from "src/modules/data-protection/assets/images/workflow.svg";
import DataFlowPanel from "./components/DataFlowPanel";
import { useMutation, useQuery } from "@tanstack/react-query";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import { toast } from "react-toastify";
import { DATA_TYPE_OPTIONS } from "src/modules/data-protection/constants";
import {
  getSubcategoryOptions,
  requiresSubcategory,
  requiresTextSubcaregory,
} from "src/modules/data-protection/pages/admin/software/utils/dataTypeOptions";
const DataFlow = ({ t, assetId }) => {
  const [activeAccordion, setActiveAccordion] = useState("");
  const [newIncomingRow, setNewIncomingRow] = useState(null);
  const [newOutgoingRow, setNewOutgoingRow] = useState(null);

  const handleAddIncomingRow = () => {
    if (newIncomingRow) return;

    setNewIncomingRow({
      id: null,
      assetId: null,
      dataType: null,
      subcategory: null,
      isNew: true,
    });
  };

  const handleAddOutgoingRow = () => {
    if (newOutgoingRow) return;

    setNewOutgoingRow({
      id: null,
      assetId: null,
      dataType: null,
      subcategory: null,
      isNew: true,
    });
  };

  const handleAccordionClick = () => {
    setActiveAccordion((prev) => (prev === "data-flow" ? null : "data-flow"));
  };

  const {
    data: softwareAssets,
    isLoading: softwareAssetsAreLoading,
    isFetching: softwareAssetsAreFetching,
    refetch: refetchSoftwareAssets
  } = useQuery({
    queryKey: ["software-assets-options"],
    queryFn: async () => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.getSoftwareAssets();
    },
    cacheTime: 0,
    enabled: !!assetId,
    refetchOnWindowFocus: false,
    onError: () => {
      toast(t("An error occurred while fetching software assets."), {
        type: "error",
      });
    },
  });

  const assetOptions = useMemo(() => {
    const rows = softwareAssets?.data?.assets || [];

    return rows.map((asset) => ({
      value: asset.id,
      label: asset.name,
    }));
  }, [softwareAssets, t]);

  const {
    data: dataFlows,
    isLoading: dataFlowsAreLoading,
    isFetching: dataFlowsAreFetching,
    refetch: refetchDataFlows,
  } = useQuery({
    queryKey: ["software-data-flows", assetId],
    queryFn: async () => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.getDataFlows(assetId);
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      toast(t("An error occurred while fetching data flows."), {
        type: "error",
      });
    },
  });

  const createDataFlowMutation = useMutation({
    mutationFn: async (payload) => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.createDataFlow(payload);
    },

    onSuccess: () => {
      toast.success(t("Data flow created successfully."));
      setNewIncomingRow(null);
      setNewOutgoingRow(null);
      refetchDataFlows();
    },

    onError: () => {
      toast.error(t("Failed to create data flow."));
    },
  });

  const handleSaveIncomingRow = (formValues) => {
    const payload = {
      software: assetId,
      sourceAsset: formValues.assetId,
      targetAsset: null,
      dataType: formValues.dataType,
      dataSubtype: formValues.subcategory || null,
      direction: "receives_from",
    };

    if (formValues.id) {
      updateDataFlowMutation.mutate({
        id: formValues.id,
        payload,
      });
    } else {
      createDataFlowMutation.mutate(payload);
    }
  };

  const handleSaveOutgoingRow = (formValues) => {
    const payload = {
      software: assetId,
      sourceAsset: null,
      targetAsset: formValues.assetId,
      dataType: formValues.dataType,
      dataSubtype: formValues.subcategory || null,
      direction: "sends_to",
    };

    if (formValues.id) {
      updateDataFlowMutation.mutate({
        id: formValues.id,
        payload,
      });
    } else {
      createDataFlowMutation.mutate(payload);
    }
  };

  const incomingFlows = useMemo(() => {
    const rows = dataFlows?.data?.receives_from || [];

    const normalizedRows = rows?.map((item) => {
      return {
        id: item.id,
        assetId: item.source_asset?.id,
        dataType: item.data_type,
        subcategory: item.data_subtype,
        isNew: false,
      };
    });

    return newIncomingRow
      ? [...normalizedRows, newIncomingRow]
      : normalizedRows;
  }, [dataFlows, newIncomingRow, t]);

  const outgoingFlows = useMemo(() => {
    const rows = dataFlows?.data?.sends_to || [];

    const normalizedRows = rows?.map((item) => ({
      id: item.id,
      assetId: item.target_asset?.id,
      dataType: item.data_type,
      subcategory: item.data_subtype,
      isNew: false,
    }));

    return newOutgoingRow
      ? [...normalizedRows, newOutgoingRow]
      : normalizedRows;
  }, [dataFlows, newOutgoingRow, t]);

  const deleteDataFlowMutation = useMutation({
    mutationFn: async (id) => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.deleteDataFlow(id);
    },
    onSuccess: () => {
      toast.success(t("Data flow deleted successfully."));
      refetchDataFlows();
    },
    onError: () => {
      toast.error(t("Failed to delete data flow."));
    },
  });

  const handleDeleteIncomingRow = (row) => {
    if (!row?.id) {
      setNewIncomingRow(null);
      return;
    }

    deleteDataFlowMutation.mutate(row.id);
  };

  const handleDeleteOutgoingRow = (row) => {
    if (!row?.id) {
      setNewOutgoingRow(null);
      return;
    }

    deleteDataFlowMutation.mutate(row.id);
  };

  const updateDataFlowMutation = useMutation({
    mutationFn: async ({ id, payload }) => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.updateDataFlow(id, payload);
    },
    onSuccess: () => {
      toast.success(t("Data flow updated successfully."));
      refetchDataFlows();
    },
    onError: () => {
      toast.error(t("Failed to update data flow."));
    },
  });

  const dataTypeOptions = useMemo(() => {
    return DATA_TYPE_OPTIONS.map((option) => ({
      value: option.value,
      label: t(option.label),
      baseLabel: option.label,
    }));
  }, [t]);

  const enrichSubcategoryOptions = useCallback(
    (dataType) =>
      getSubcategoryOptions(dataType).map((option) => ({
        value: option.value,
        label: t(option.label),
        baseLabel: option.label,
      })),
    [t]
  );

  return (
    <Accordion activeKey={activeAccordion} className="mt-4 data-flow">
      <Accordion.Item
        eventKey="data-flow"
        className={activeAccordion === "data-flow" ? "active" : ""}
      >
        <Accordion.Header onClick={handleAccordionClick}>
          <img
            src={DataFlowImage}
            alt=""
            className="data-flow__header-icon me-2"
          />
          <span>{t("Data Flow")}</span>
        </Accordion.Header>

        <Accordion.Body>
          <Row className="g-3">
            <Col xs={12} lg={6}>
              <DataFlowPanel
                title={t("Receives Data From")}
                emptyText={t("No incoming data flows added yet.")}
                flows={incomingFlows}
                assetOptions={assetOptions}
                dataTypeOptions={dataTypeOptions}
                getSubcategoryOptions={enrichSubcategoryOptions}
                requiresSubcategory={requiresSubcategory}
                requiresTextSubcategory={requiresTextSubcaregory}
                onAddClick={handleAddIncomingRow}
                onSaveRow={handleSaveIncomingRow}
                onDeleteRow={handleDeleteIncomingRow}
                isLoading={
                  dataFlowsAreLoading ||
                  dataFlowsAreFetching ||
                  softwareAssetsAreLoading ||
                  softwareAssetsAreFetching
                }
                error={null}
                isSaving={
                  createDataFlowMutation.isPending ||
                  updateDataFlowMutation.isPending
                }
                isDeleting={deleteDataFlowMutation.isPending}
              />
            </Col>

            <Col xs={12} lg={6}>
              <DataFlowPanel
                title={t("Sends Data To")}
                emptyText={t("No outgoing data flows added yet.")}
                flows={outgoingFlows}
                assetOptions={assetOptions}
                dataTypeOptions={dataTypeOptions}
                getSubcategoryOptions={enrichSubcategoryOptions}
                requiresSubcategory={requiresSubcategory}
                requiresTextSubcategory={requiresTextSubcaregory}
                onAddClick={handleAddOutgoingRow}
                onSaveRow={handleSaveOutgoingRow}
                onDeleteRow={handleDeleteOutgoingRow}
                isLoading={
                  dataFlowsAreLoading ||
                  dataFlowsAreFetching ||
                  softwareAssetsAreLoading ||
                  softwareAssetsAreFetching
                }
                error={null}
                isSaving={
                  createDataFlowMutation.isPending ||
                  updateDataFlowMutation.isPending
                }
                isDeleting={deleteDataFlowMutation.isPending}
              />
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default withNamespaces()(DataFlow);
